import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { VerdictBadge } from "@/components/RiskBadge";
import GenerateTokenModal from "@/components/GenerateTokenModal";
import { useAuth } from "@/context/AuthContext";
import { ScanSearch, ShieldAlert, ShieldCheck, ShieldQuestion, KeyRound, Plus, ArrowRight, Users } from "lucide-react";

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="card-soc p-5 fade-up" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="font-head text-4xl font-extrabold mt-3" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async () => {
    const [s, sc] = await Promise.all([api.get("/dashboard/stats"), api.get("/scans")]);
    setStats(s.data);
    setScans(sc.data.slice(0, 6));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="eyebrow mb-2">SECURITY OVERVIEW</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Command Center</h1>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="generate-token-modal-button"
          className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04121a] font-semibold rounded-lg px-4 py-2.5 transition-colors"
        >
          <Plus className="w-4 h-4" /> Generate Scan Token
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <StatCard icon={ScanSearch} label="Total Scans" value={stats?.total_scans ?? "–"} color="#06B6D4" testid="stat-total-scans" />
        <StatCard icon={ShieldAlert} label="High Risk" value={stats?.high_risk ?? "–"} color="#EF4444" testid="stat-high-risk" />
        <StatCard icon={ShieldQuestion} label="Suspicious" value={stats?.suspicious ?? "–"} color="#F59E0B" testid="stat-suspicious" />
        <StatCard icon={ShieldCheck} label="Clear" value={stats?.clear ?? "–"} color="#10B981" testid="stat-clear" />
        <StatCard icon={KeyRound} label="Active Tokens" value={stats?.active_tokens ?? "–"} color="#3B82F6" testid="stat-active-tokens" />
        {user?.role === "admin" ? (
          <StatCard icon={Users} label="Clients" value={stats?.clients ?? "–"} color="#A78BFA" testid="stat-clients" />
        ) : (
          <StatCard
            icon={KeyRound}
            label="Tokens Left"
            value={stats ? (stats.token_quota == null ? "∞" : stats.tokens_remaining) : "–"}
            color="#A78BFA"
            testid="stat-tokens-left"
          />
        )}
      </div>

      <div className="card-soc overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
          <h2 className="font-head font-bold text-lg">Recent Scans</h2>
          <button
            onClick={() => navigate("/scans")}
            className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          >
            View all <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {scans.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">
            No scans yet. Generate a token, send the candidate link, and results appear here automatically once the scanner runs.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="recent-scans-table">
            <thead>
              <tr className="text-left eyebrow border-b border-white/8">
                <th className="px-6 py-3 font-normal">Candidate</th>
                <th className="px-6 py-3 font-normal">Host</th>
                <th className="px-6 py-3 font-normal">OS</th>
                <th className="px-6 py-3 font-normal">Detections</th>
                <th className="px-6 py-3 font-normal">Verdict</th>
                <th className="px-6 py-3 font-normal">Score</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => navigate(`/scans/${s.id}`)}
                  data-testid={`scan-row-${s.id}`}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-3.5 text-slate-100 font-medium">{s.candidate_name}</td>
                  <td className="px-6 py-3.5 text-slate-400 font-mono text-xs">{s.hostname || "—"}</td>
                  <td className="px-6 py-3.5 text-slate-400 text-xs">{s.os_name || "—"}</td>
                  <td className="px-6 py-3.5 text-slate-300">{s.detections?.length ?? 0}</td>
                  <td className="px-6 py-3.5"><VerdictBadge verdict={s.verdict} /></td>
                  <td className="px-6 py-3.5 font-mono text-slate-200">{s.risk_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <GenerateTokenModal open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}
